import React, { useState, useEffect } from 'react';
import API from '../api/api';
import EDIModal from './EDIModal';
import RateConfirmationModal from './RateConfirmationModal';

function ShipmentDetailsModal({ isOpen, onClose, shipment, onShipmentUpdated, onInvoiceModalClose }) {
  const [formData, setFormData] = useState({
    pickup_date: '',
    pickup_time: '',
    delivery_date: '',
    delivery_time: '',
    actual_pickup_date: '',
    actual_delivery_date: '',
    equipment_type: '',
    rate: '',
    cost: '',
    first_mile: '',
    last_mile: '',
    trailer_number: '',
    truck_number: '',
    driver: '',
    special_instructions: ''
  });

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // EDI change response states
  const [showChangeResponse, setShowChangeResponse] = useState(false);
  const [changeDeclineReason, setChangeDeclineReason] = useState('');

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [rateConModalOpen, setRateConModalOpen] = useState(false);

  // OpenDock pickup appointment booking
  const [dockAppointments, setDockAppointments] = useState([]);
  const [bookingAppointment, setBookingAppointment] = useState(false);
  const [appointmentError, setAppointmentError] = useState(null);

  const equipmentTypes = [
    'DRY_VAN', 'REEFER', 'FLATBED', 'POWER_ONLY', 'STEP_DECK', 
    'CONESTOGA', 'LOWBOY', 'TANKER', 'BULK', 'OTHER', 'UNKNOWN'
  ];

  useEffect(() => {
    if (!isOpen) return;

    // Fetch org's active drivers for the assignment dropdown
    API.get('drivers/', { params: { is_active: true } })
      .then(res => setDrivers(res.data?.results ?? res.data))
      .catch(() => setDrivers([]));

    if (shipment) {
      setFormData({
        pickup_date: shipment.pickup_date || '',
        pickup_time: shipment.pickup_time || '',
        delivery_date: shipment.delivery_date || '',
        delivery_time: shipment.delivery_time || '',
        actual_pickup_date: shipment.actual_pickup_date || '',
        actual_delivery_date: shipment.actual_delivery_date || '',
        equipment_type: shipment.equipment_type || '',
        rate: shipment.rate || '',
        cost: shipment.cost || '',
        first_mile: shipment.first_mile || '',
        last_mile: shipment.last_mile || '',
        trailer_number: shipment.trailer_number || '',
        truck_number: shipment.truck_number || '',
        driver: shipment.driver ?? '',
        special_instructions: shipment.special_instructions || ''
      });
      setError(null);
      setSuccess(false);
      setDockAppointments(shipment.dock_appointments || []);
      setAppointmentError(null);
    }
  }, [isOpen, shipment]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Clean up formData - convert empty strings to null for optional fields
      const cleanedData = { ...formData };
      
      // Convert empty strings to null for date/time fields
      const dateTimeFields = ['pickup_date', 'pickup_time', 'delivery_date', 'delivery_time', 
                              'actual_pickup_date', 'actual_delivery_date'];
      dateTimeFields.forEach(field => {
        if (cleanedData[field] === '') {
          cleanedData[field] = null;
        }
      });
      
      // Convert empty strings to null or remove for other optional fields
      ['equipment_type', 'rate', 'cost', 'first_mile', 'last_mile',
       'trailer_number', 'truck_number', 'driver', 'special_instructions'].forEach(field => {
        if (cleanedData[field] === '') {
          cleanedData[field] = null;
        }
      });

      const response = await API.patch(`shipments/${shipment.id}/`, cleanedData);
      setSuccess(true);
      setTimeout(() => {
        onShipmentUpdated(response.data);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error updating shipment:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      
      // Handle different error formats
      let errorMessage = 'Failed to update shipment';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else {
          // Show all field errors
          errorMessage = Object.entries(err.response.data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(false);
    setShowChangeResponse(false);
    setChangeDeclineReason('');
    setInvoiceModalOpen(false);
    setRateConModalOpen(false);
    setAppointmentError(null);
    onClose();
  };

  // Book an OpenDock pickup appointment for this shipment's load
  const handleBookPickup = async () => {
    if (!shipment?.load || bookingAppointment) return;
    setBookingAppointment(true);
    setAppointmentError(null);
    try {
      const response = await API.post(`loads/${shipment.load}/book-pickup-appointment/`);
      setDockAppointments((prev) => [response.data, ...prev]);
    } catch (err) {
      console.error('Error booking OpenDock appointment:', err);
      setAppointmentError(err.response?.data?.error || 'Failed to book appointment. Please try again.');
    } finally {
      setBookingAppointment(false);
    }
  };

  const handleInvoiceModalClose = () => {
    setInvoiceModalOpen(false);
    onInvoiceModalClose?.();
  };

  const handleAcceptChanges = async () => {
    if (!window.confirm('Accept these EDI changes? A 990 acceptance will be sent to the shipper.')) {
      return;
    }
    
    try {
      setLoading(true);
      const requestData = { accepted: true };
      
      // Send 990 acceptance via the shipment change response endpoint
      await API.post(`shipments/${shipment.pro_number}/respond-to-changes/`, requestData);
      
      alert('EDI changes accepted. 990 acceptance sent to shipper.');
      
      onShipmentUpdated({ ...shipment, has_pending_changes: false });
      handleClose();
    } catch (err) {
      console.error('Error accepting changes:', err);
      setError(err.response?.data?.error || 'Failed to accept changes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineChanges = async () => {
    if (!changeDeclineReason) {
      alert('Please select a decline reason');
      return;
    }
    
    if (!window.confirm('Decline these EDI changes? A 990 decline will be sent to the shipper.')) {
      return;
    }
    
    try {
      setLoading(true);
      const requestData = { 
        accepted: false,
        decline_reason: changeDeclineReason
      };
      
      // Send 990 decline via the shipment change response endpoint
      await API.post(`shipments/${shipment.pro_number}/respond-to-changes/`, requestData);
      
      alert(`EDI changes declined (${changeDeclineReason}). 990 decline sent to shipper.`);
      
      onShipmentUpdated({ ...shipment, has_pending_changes: false });
      handleClose();
    } catch (err) {
      console.error('Error declining changes:', err);
      setError(err.response?.data?.error || 'Failed to decline changes');
    } finally {
      setLoading(false);
    }
  };

  // EDI 990 routing is handled in the backend; no vendor selection in UI

  if (!isOpen || !shipment) return null;

  return (
    <>
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Shipment Details - PRO #{shipment.pro_number}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Shipment updated successfully!
                  </h3>
                </div>
              </div>
            </div>
          )}

          {/* EDI Changes Display */}
          {shipment.has_pending_changes && (
            <div className="mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
              <div className="flex items-start mb-4">
                <svg className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-yellow-900 mb-2">EDI Change Received</h4>
                  <p className="text-sm text-yellow-800 mb-3">
                    This shipment has been modified by the shipper ({shipment.edi_change_count || 1} change{shipment.edi_change_count > 1 ? 's' : ''} received).
                    Please review the changes below.
                  </p>
                  <div className="text-xs text-yellow-700 mb-3">
                    <strong>Last updated:</strong> {shipment.last_edi_change_at ? new Date(shipment.last_edi_change_at).toLocaleString() : 'N/A'}
                  </div>
                </div>
              </div>
              
              {/* Display Changed Fields */}
              {(() => {
                const lc = shipment.latest_change;
                const fieldChanges = lc?.changed_fields?.length > 0;
                const stopChanges = lc?.stops_changes?.changes?.length > 0;
                const STOP_FIELD_LABELS = {
                  order_line_items: 'Order Line Items',
                  contact_email: 'Contact Email',
                };
                if (!lc || (!fieldChanges && !stopChanges)) {
                  return (
                    <div className="bg-white rounded-md p-3 border border-yellow-300 text-sm text-gray-600">
                      Changes are pending review. Full change details will appear here.
                    </div>
                  );
                }
                return (
                  <div className="bg-white rounded-md p-4 border border-yellow-300">
                    <h5 className="text-sm font-semibold text-gray-900 mb-3">Changes Summary:</h5>
                    {fieldChanges && (
                      <div className="space-y-3">
                        {lc.changed_fields.map((field, idx) => {
                          const oldValue = lc.old_values?.[field];
                          const newValue = lc.new_values?.[field];
                          const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                          return (
                            <div key={idx} className="flex items-start text-sm border-b border-gray-200 pb-2 last:border-0">
                              <div className="flex-1">
                                <span className="font-medium text-gray-700">{fieldLabel}:</span>
                                <div className="mt-1 grid grid-cols-2 gap-2">
                                  <div className="bg-red-50 border border-red-200 rounded px-2 py-1">
                                    <span className="text-xs text-red-600 font-semibold block">Old:</span>
                                    <span className="text-red-800">{oldValue || 'N/A'}</span>
                                  </div>
                                  <div className="bg-green-50 border border-green-200 rounded px-2 py-1">
                                    <span className="text-xs text-green-600 font-semibold block">New:</span>
                                    <span className="text-green-800">{newValue || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {stopChanges && (
                      <div className={fieldChanges ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                        <h6 className="text-sm font-semibold text-gray-900 mb-2">Stop Changes:</h6>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                          {lc.stops_changes.changes.map((change, idx) => {
                            if (change.action === 'added') return <li key={idx}>Stop {change.stop_sequence} added: {change.name}</li>;
                            if (change.action === 'removed') return <li key={idx}>Stop {change.stop_sequence} removed: {change.name}</li>;
                            const label = STOP_FIELD_LABELS[change.field] || change.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            if (change.field === 'order_line_items' && (change.old_items || change.new_items)) {
                              const formatItem = (item) => [
                                item.order_number && `PO# ${item.order_number}`,
                                item.line_item_number && `Line ${item.line_item_number}`,
                                item.description,
                                item.quantity && item.package_type && `${item.quantity} ${item.package_type}`,
                                item.weight && `${item.weight} ${item.weight_unit || 'lbs'}`,
                              ].filter(Boolean).join(' · ');
                              return (
                                <li key={idx}>
                                  <span>Stop {change.stop_sequence} — {label}:</span>
                                  <ul className="ml-4 mt-1 space-y-0.5">
                                    {(change.old_items || []).map((item, i) => <li key={`old-${i}`} className="text-red-600 line-through text-xs">{formatItem(item)}</li>)}
                                    {(change.new_items || []).map((item, i) => <li key={`new-${i}`} className="text-green-700 font-medium text-xs">{formatItem(item)}</li>)}
                                  </ul>
                                </li>
                              );
                            }
                            return <li key={idx}>Stop {change.stop_sequence} — {label}: {change.old_value} → {change.new_value}</li>;
                          })}
                        </ul>
                      </div>
                    )}

                  {/* Decline Reason Selection (only show if declining) */}
                  {showChangeResponse && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                      <h6 className="text-xs font-semibold text-red-800 mb-2">Decline Reason (Required)</h6>
                      <select
                        value={changeDeclineReason}
                        onChange={(e) => setChangeDeclineReason(e.target.value)}
                        className="block w-full px-3 py-2 text-sm border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <option value="">Select a reason...</option>
                        <option value="T06">No capacity available</option>
                        <option value="T05">Trailer class not available</option>
                        <option value="W15">Wrong equipment type</option>
                        <option value="WLT">Weight limit exceeded</option>
                        <option value="A81">Not avail on requested date</option>
                        <option value="RUN">Reason unknown</option>
                        <option value="ZZZ">Other (mutually defined)</option>
                        <option value="015">Default / General decline</option>
                      </select>
                    </div>
                  )}
                  
                  <div className="mt-4 flex justify-end items-center">
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={handleAcceptChanges}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                      >
                        Accept Changes (Send 990)
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!showChangeResponse) {
                            setShowChangeResponse(true);
                          } else {
                            handleDeclineChanges();
                          }
                        }}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        {showChangeResponse ? 'Confirm Decline (Send 990)' : 'Decline Changes'}
                      </button>
                    </div>
                  </div>
                </div>
                );
              })()}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Read-only Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Shipment Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600">PRO Number</label>
                  <p className="text-sm text-gray-900">{shipment.pro_number}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">PO Number</label>
                  <p className="text-sm text-gray-900">{shipment.po_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Customer PO</label>
                  <p className="text-sm text-gray-900">{shipment.cust_po_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Shipper</label>
                  <p className="text-sm text-gray-900">{shipment.shipper_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Carrier</label>
                  <p className="text-sm text-gray-900">{shipment.carrier_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Status</label>
                  <p className="text-sm text-gray-900 capitalize">{shipment.status?.replace('_', ' ') || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Editable Fields */}
            <div className="bg-white border rounded-lg p-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Schedule & Dates</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="pickup_date" className="block text-sm font-medium text-gray-700">
                    Scheduled Pickup Date
                  </label>
                  <input
                    type="date"
                    name="pickup_date"
                    id="pickup_date"
                    value={formData.pickup_date}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="pickup_time" className="block text-sm font-medium text-gray-700">
                    Pickup Time
                  </label>
                  <input
                    type="time"
                    name="pickup_time"
                    id="pickup_time"
                    value={formData.pickup_time}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="delivery_date" className="block text-sm font-medium text-gray-700">
                    Scheduled Delivery Date
                  </label>
                  <input
                    type="date"
                    name="delivery_date"
                    id="delivery_date"
                    value={formData.delivery_date}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="delivery_time" className="block text-sm font-medium text-gray-700">
                    Delivery Time
                  </label>
                  <input
                    type="time"
                    name="delivery_time"
                    id="delivery_time"
                    value={formData.delivery_time}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="actual_pickup_date" className="block text-sm font-medium text-gray-700">
                    Actual Pickup Date
                  </label>
                  <input
                    type="date"
                    name="actual_pickup_date"
                    id="actual_pickup_date"
                    value={formData.actual_pickup_date}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="actual_delivery_date" className="block text-sm font-medium text-gray-700">
                    Actual Delivery Date
                  </label>
                  <input
                    type="date"
                    name="actual_delivery_date"
                    id="actual_delivery_date"
                    value={formData.actual_delivery_date}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Equipment & Rates</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="equipment_type" className="block text-sm font-medium text-gray-700">
                    Equipment Type
                  </label>
                  <select
                    name="equipment_type"
                    id="equipment_type"
                    value={formData.equipment_type}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select Equipment Type</option>
                    {equipmentTypes.map(type => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                {shipment.temperature_mode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Temperature Mode</label>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{shipment.temperature_mode.toLowerCase()}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="rate" className="block text-sm font-medium text-gray-700">
                    Total Rate ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="rate"
                    id="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="cost" className="block text-sm font-medium text-gray-700">
                    Cost ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="cost"
                    id="cost"
                    value={formData.cost}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="first_mile" className="block text-sm font-medium text-gray-700">
                    First Mile
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="first_mile"
                    id="first_mile"
                    value={formData.first_mile}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="last_mile" className="block text-sm font-medium text-gray-700">
                    Last Mile
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="last_mile"
                    id="last_mile"
                    value={formData.last_mile}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="trailer_number" className="block text-sm font-medium text-gray-700">
                    Trailer Number
                  </label>
                  <input
                    type="text"
                    name="trailer_number"
                    id="trailer_number"
                    value={formData.trailer_number}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="truck_number" className="block text-sm font-medium text-gray-700">
                    Truck Number
                  </label>
                  <input
                    type="text"
                    name="truck_number"
                    id="truck_number"
                    value={formData.truck_number}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="driver" className="block text-sm font-medium text-gray-700">
                    Assigned Driver
                  </label>
                  <select
                    name="driver"
                    id="driver"
                    value={formData.driver}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">— Unassigned —</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.full_name}{d.phone ? ` · ${d.phone}` : ''}
                      </option>
                    ))}
                  </select>
                  {drivers.length === 0 && (
                    <p className="mt-1 text-xs text-gray-400">
                      No active drivers found. Add drivers under Settings → Drivers.
                    </p>
                  )}
                </div>
              </div>

              {/* Rate line items breakdown — read-only, only shown when EDI provided line items */}
              {shipment.rate_line_items && shipment.rate_line_items.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">EDI Rate Breakdown</p>
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Description</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {shipment.rate_line_items.map((item, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-700">{item.description || '—'}</td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900">
                              ${Number(item.amount).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-semibold border-t border-gray-200">
                          <td className="px-3 py-2 text-gray-800">Total</td>
                          <td className="px-3 py-2 text-right text-gray-900">
                            ${Number(shipment.rate || 0).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Sourced from EDI tender — edit Total Rate above to override.</p>
                </div>
              )}
            </div>

            <div className="bg-white border rounded-lg p-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Additional Information</h4>
              <div>
                <label htmlFor="special_instructions" className="block text-sm font-medium text-gray-700">
                  Special Instructions
                </label>
                <textarea
                  name="special_instructions"
                  id="special_instructions"
                  rows="3"
                  value={formData.special_instructions}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter any special instructions..."
                />
              </div>
            </div>

            {/* Location Information (Read-only) */}
            {(shipment.origin_name || shipment.destination_name) && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Locations</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {shipment.origin_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Pickup Location</label>
                      <p className="text-sm text-gray-900">{shipment.origin_name}</p>
                      {shipment.origin_address && (
                        <p className="text-xs text-gray-500 mt-1">{shipment.origin_address}</p>
                      )}
                    </div>
                  )}
                  {shipment.destination_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Delivery Location</label>
                      <p className="text-sm text-gray-900">{shipment.destination_name}</p>
                      {shipment.destination_address && (
                        <p className="text-xs text-gray-500 mt-1">{shipment.destination_address}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Stop Details — contact email & order line items */}
            {shipment.stops && shipment.stops.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Stop Details</h4>
                <div className="space-y-4">
                  {shipment.stops.map((stop) => (
                    <div key={stop.id} className="bg-white border rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase ${['PU','CL','LD','PL'].includes(stop.stop_type) ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {['PU','CL','LD','PL'].includes(stop.stop_type) ? 'Pickup' : 'Delivery'}
                        </span>
                        <span className="text-sm font-medium text-gray-800">Stop {stop.sequence}{stop.name ? ` — ${stop.name}` : ''}</span>
                      </div>
                      {stop.contact_email && (
                        <p className="text-xs text-gray-500 mb-2">
                          Contact: <a href={`mailto:${stop.contact_email}`} className="text-blue-600 hover:underline">{stop.contact_email}</a>
                        </p>
                      )}
                      {stop.order_line_items && stop.order_line_items.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Order Line Items</p>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-xs border border-gray-100 rounded">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-2 py-1 text-left text-gray-500 font-semibold">Order #</th>
                                  <th className="px-2 py-1 text-left text-gray-500 font-semibold">Description</th>
                                  <th className="px-2 py-1 text-right text-gray-500 font-semibold">Qty</th>
                                  <th className="px-2 py-1 text-right text-gray-500 font-semibold">Weight</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {stop.order_line_items.map((item, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-2 py-1 text-gray-700">{item.order_number || item.line_item_number || '—'}</td>
                                    <td className="px-2 py-1 text-gray-600">{item.description || '—'}</td>
                                    <td className="px-2 py-1 text-right text-gray-700">{item.quantity != null ? item.quantity : '—'}</td>
                                    <td className="px-2 py-1 text-right text-gray-700">{item.weight != null ? `${item.weight} ${item.weight_unit || ''}`.trim() : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pickup Appointment (OpenDock automation) */}
            {shipment.load && (
              <div className="bg-white border rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Pickup Appointment</h4>
                {dockAppointments.length > 0 ? (
                  <div className="space-y-2">
                    {dockAppointments.map((appt) => (
                      <div
                        key={appt.id}
                        className={`rounded-lg border p-3 text-sm ${
                          appt.status === 'booked'
                            ? 'bg-green-50 border-green-200'
                            : appt.status === 'failed'
                            ? 'bg-red-50 border-red-200'
                            : 'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">
                            {(appt.platform_display || appt.platform)} — {appt.direction === 'pickup' ? 'Pickup' : 'Delivery'}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              appt.status === 'booked'
                                ? 'bg-green-100 text-green-800'
                                : appt.status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {appt.status_display || appt.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                          {appt.warehouse_name && <div><strong>Warehouse:</strong> {appt.warehouse_name}</div>}
                          {appt.appointment_start && (
                            <div><strong>Appointment:</strong> {new Date(appt.appointment_start).toLocaleString()}</div>
                          )}
                          {appt.confirmation_number && <div><strong>Confirmation #:</strong> {appt.confirmation_number}</div>}
                          <div><strong>Booked by:</strong> {appt.booked_by}{appt.booked_at ? ` on ${new Date(appt.booked_at).toLocaleString()}` : ''}</div>
                          {appt.error_message && <div className="text-red-600">{appt.error_message}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No dock appointment booked for this shipment yet.</p>
                )}
                {!dockAppointments.some(
                  (a) => a.platform === 'opendock' && a.direction === 'pickup' && ['pending', 'requested', 'booked'].includes(a.status)
                ) && (
                  <button
                    type="button"
                    onClick={handleBookPickup}
                    disabled={bookingAppointment}
                    className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {bookingAppointment ? 'Booking…' : 'Book Pickup Appointment (OpenDock)'}
                  </button>
                )}
                {appointmentError && <p className="text-xs text-red-600 mt-2">{appointmentError}</p>}
              </div>
            )}

            {/* Form Actions */}
            <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => setRateConModalOpen(true)}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Rate Confirmation
              </button>
              <button
                type="button"
                onClick={() => setInvoiceModalOpen(true)}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Create Invoice
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update Shipment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <EDIModal
      isOpen={invoiceModalOpen}
      onClose={handleInvoiceModalClose}
      shipment={shipment}
      ediType="210"
      canSend210={shipment?.can_send_edi_210 ?? false}
    />

    <RateConfirmationModal
      isOpen={rateConModalOpen}
      onClose={() => setRateConModalOpen(false)}
      shipment={shipment}
      onSent={() => onShipmentUpdated?.(shipment)}
    />
    </>
  );
}

export default ShipmentDetailsModal;

